import urljoin from 'urljoin';

import { getClientId } from '../utils/client';
import { delegate } from '../utils/delegate';

import { setAuth } from './auth';

const MAX_RETRIES = 5;

/**
 * Stringifies query parameters and append them to the url
 * @param  {string} url  - an url
 * @param  {object} data - an object containing the query parameters
 * @return {string}      - the final url
 */
export function stringifyGETParams(url, data) {
    const query = Object.keys(data).reduce((q, key) => {
        if (data[key] !== null) {
            return q + '&' + encodeURIComponent(key) + '=' + encodeURIComponent(data[key]);
        }
        return q;
    }, '');

    if (query) {
        url += (~url.indexOf('?') ? '&' : '?') + query.substring(1);
    }
    return url;
}

export function handleResponse(response, dispatch, attempt, callback) {
    const status = response.status;

    if (status === 204) {
        return Promise.resolve();
    }

    const contentType = response.headers.get('Content-Type') || '';
    const isJson = contentType.indexOf('application/json') > -1;

    if (status >= 200 && status < 300) {
        return isJson ? response.json() : Promise.resolve();
    }

    if (status === 401 && attempt < MAX_RETRIES && delegate.hasOnInvalidAuth()) {
        return delegate.onInvalidAuth().then((jwt) => {
            if (jwt) {
                dispatch(setAuth({ jwt }));
            }

            return callback();
        });
    }

    if (isJson) {
        return response.json().then(function (json) {
            const { error = {} } = json;
            error.status = status;
            return Promise.reject(error);
        });
    }

    const error = new Error(response.statusText);
    error.response = response;
    return Promise.reject(error);
}

const doRequest = (method, url, data, extraHeaders, baseUrl, attempt = 0) => {
    return (dispatch, getState) => {
        method = method.toUpperCase();
        const { auth, config, user } = getState();

        const headers = {
            Accept: 'application/json',
            'Content-Type': 'application/json',
            'x-smooch-sdk': `web/${VENDOR_ID}/${VERSION}`,
            'x-smooch-appid': config.appId,
            ...extraHeaders,
        };

        if (config.integrationId) {
            // integrationId may only be available after GET /:appId/config gets called.
            headers['x-smooch-clientid'] = getClientId(config.integrationId);
        }

        if (auth.jwt) {
            headers.Authorization = `Bearer ${auth.jwt}`;
        } else if (auth.sessionToken) {
            headers.Authorization = `Basic ${btoa(`${user._id}:${auth.sessionToken}`)}`;
        }

        const fetchOptions = {
            method: method,
            headers,
        };

        if (data) {
            if (data instanceof FormData) {
                fetchOptions.body = data;
                // Remove the Content-Type header, `fetch` will
                // generate one to add the form boundary.
                delete fetchOptions.headers['Content-Type'];
            } else {
                data = Object.assign({}, data);
                if (method === 'GET') {
                    url = stringifyGETParams(url, data);
                } else if (method === 'POST' || method === 'PUT') {
                    fetchOptions.body = JSON.stringify(data);
                }
            }
        }
        const fullUrl = urljoin(baseUrl || config.apiBaseUrl, url);

        const retryCallback = () => {
            return dispatch(doRequest(method, url, data, extraHeaders, baseUrl, attempt + 1));
        };

        return global
            .fetch(fullUrl, fetchOptions)
            .then((response) => handleResponse(response, dispatch, attempt, retryCallback));
    };
};

export default function http(method, url, data, extraHeaders = {}, baseUrl) {
    return doRequest(method, url, data, extraHeaders, baseUrl);
}
